import { useEffect, useMemo, useState } from "react";
import {
  Coins,
  Users,
  Grid3x3,
  ShieldCheck,
  CalendarRange,
  ClipboardCheck,
  ArrowRight,
  Info,
  DoorOpen,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { EVENTO_WIZARD, EVENTO_VENTA, EVENTO_SALONES_AG } from "@/constants/testIds";
import {
  ESTADO_MESA,
  ESTADO_MESA_LABEL,
  TIPO_ACCESO_LABEL,
  formatearFechaCorta,
  agruparMesasPorEstado,
  salonesUtilizados,
} from "@/data/adminEventosStore";
import {
  listarSalonesAcceso,
  ordenarSalones,
  resumenSalonesAcceso,
} from "@/data/salonesAccesoStore";

const fmtDinero = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "$ —";
  return num.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 2,
  });
};

/**
 * Paso 4 · Configuración comercial (solo lectura).
 *
 * Cambios Fase B: los precios ya no se capturan aquí. Se muestran los
 * definidos en el Paso 1. Para modificarlos, botón "Editar precios"
 * que regresa al Paso 1.
 */
export default function PasoVenta({ valores, errores, onGoToStep }) {
  const esAG = (valores.modalidad || "MESAS") === "ACCESO_GENERAL";
  const mesas = Array.isArray(valores.mesas) ? valores.mesas : [];
  const precios =
    valores.precios && typeof valores.precios === "object"
      ? valores.precios
      : {};
  const salones = useMemo(() => salonesUtilizados(valores), [valores]);
  const desglose = useMemo(() => agruparMesasPorEstado(mesas), [mesas]);

  const [salonesAG, setSalonesAG] = useState(
    ordenarSalones(valores.salonesAcceso || []),
  );
  useEffect(() => {
    let cancelado = false;
    if (esAG && valores.id) {
      listarSalonesAcceso(valores.id).then((lista) => {
        if (!cancelado) setSalonesAG(ordenarSalones(lista));
      });
    }
    return () => {
      cancelado = true;
    };
  }, [esAG, valores.id]);
  const resumenAG = useMemo(() => resumenSalonesAcceso(salonesAG), [salonesAG]);

  if (esAG) {
    return (
      <div
        data-testid={EVENTO_WIZARD.stepContent(4)}
        className="flex flex-col gap-6"
      >
        <header>
          <span className="overline text-accent">Paso 4</span>
          <h2 className="mt-1 font-heading text-2xl sm:text-3xl leading-tight">
            Resumen comercial · Acceso General
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
            Verifica los salones vendibles, su capacidad y precio antes de
            publicar. Para editarlos, regresa a «Salones y boletos».
          </p>
        </header>

        <section
          data-testid={EVENTO_SALONES_AG.resumen}
          className="rounded-2xl border hairline bg-card p-6 flex flex-col gap-5"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <ResumenAGDato label="Modalidad" valor="Acceso General" Icono={DoorOpen} />
            <ResumenAGDato label="Salones activos" valor={resumenAG.salonesActivos} Icono={Grid3x3} />
            <ResumenAGDato
              label="Capacidad vendible"
              valor={`${resumenAG.capacidadTotalActiva}`}
              testId={EVENTO_SALONES_AG.resumenCapacidad}
              Icono={Users}
            />
            <ResumenAGDato
              label="Máx. por socio"
              valor={`${valores.limiteBoletosPorUsuario ?? "—"}`}
              Icono={ShieldCheck}
            />
          </div>

          <div className="border-t hairline pt-4">
            <p className="overline text-muted-foreground mb-3">Salones</p>
            {salonesAG.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aún no hay salones configurados.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {salonesAG.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-3 rounded-lg border hairline bg-background px-3 py-2"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-sm truncate">{s.salon}</span>
                      <span
                        className={cn(
                          "text-[10px] uppercase tracking-wide font-semibold rounded-full px-2 py-0.5 ring-1",
                          s.activo
                            ? "text-emerald-700 dark:text-emerald-300 ring-emerald-500/40 bg-emerald-500/10"
                            : "text-muted-foreground ring-border bg-muted",
                        )}
                      >
                        {s.activo ? "Activo" : "Inactivo"}
                      </span>
                    </span>
                    <span className="text-sm tabular-nums whitespace-nowrap">
                      {s.capacidad} lugares · {fmtDinero(s.precioPorBoleto)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => onGoToStep(3)}
              className="rounded-xl"
            >
              Editar salones en Paso 3
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      data-testid={EVENTO_WIZARD.stepContent(4)}
      className="flex flex-col gap-6"
    >
      <header>
        <span className="overline text-accent">Paso 4</span>
        <h2 className="mt-1 font-heading text-2xl sm:text-3xl leading-tight">
          Configuración comercial
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Revisa precios por salón (definidos en el Paso 1), capacidad derivada
          del plano y reglas de compra antes de publicar.
        </p>
      </header>

      {errores.capacidad && (
        <p
          data-testid={EVENTO_VENTA.errorCapacidad}
          className="text-sm text-destructive -mt-2"
        >
          {errores.capacidad}
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna izquierda · Configuración */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          <Seccion
            icon={Coins}
            titulo="Precios por salón"
            descripcion="Se definen en el Paso 1. Para cambiarlos, regresa al Paso 1."
          >
            {salones.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Selecciona al menos un salón en el Paso 1.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {salones.map((s) => (
                  <div
                    key={s}
                    className="flex items-center gap-3 rounded-xl border hairline bg-background px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{s}</p>
                      <p className="text-[11px] text-muted-foreground">
                        Precio por boleto en este salón.
                      </p>
                    </div>
                    <p
                      data-testid={EVENTO_VENTA.precioSalon(s)}
                      className={cn(
                        "font-heading text-xl tabular-nums",
                        precios[s] > 0
                          ? "text-primary dark:text-accent"
                          : "text-destructive",
                      )}
                    >
                      {fmtDinero(precios[s])}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onGoToStep(1)}
                className="rounded-xl"
              >
                Editar precios en Paso 1
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Seccion>

          <Seccion
            icon={Users}
            titulo="Capacidad"
            descripcion="Se calcula automáticamente a partir del plano de mesas (Paso 3)."
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatBlock
                testId={EVENTO_VENTA.capacidadTotal}
                label="Total"
                value={desglose.capacidad.total}
                tone="primary"
              />
              <StatBlock
                testId={EVENTO_VENTA.capacidadDisponible}
                label="Disponible"
                value={desglose.capacidad.DISPONIBLE}
                tone="success"
              />
              <StatBlock
                testId={EVENTO_VENTA.capacidadReservada}
                label="Reservada"
                value={desglose.capacidad.RESERVADA}
                tone="warning"
              />
              <StatBlock
                testId={EVENTO_VENTA.capacidadProtocolo}
                label="Protocolo"
                value={desglose.capacidad.PROTOCOLO}
                tone="accent"
              />
              <StatBlock
                testId={EVENTO_VENTA.capacidadBloqueada}
                label="Bloqueada"
                value={desglose.capacidad.BLOQUEADA}
                tone="danger"
              />
            </div>
          </Seccion>

          <Seccion icon={Grid3x3} titulo="Mesas">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.values(ESTADO_MESA).map((e) => (
                <StatBlock
                  key={e}
                  testId={EVENTO_VENTA.mesasPorEstado(e)}
                  label={ESTADO_MESA_LABEL[e]}
                  value={desglose.mesas[e]}
                  tone={
                    e === ESTADO_MESA.DISPONIBLE
                      ? "success"
                      : e === ESTADO_MESA.RESERVADA
                        ? "warning"
                        : e === ESTADO_MESA.PROTOCOLO
                          ? "accent"
                          : "danger"
                  }
                />
              ))}
            </div>
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                data-testid={EVENTO_VENTA.btnAdministrarMesas}
                onClick={() => onGoToStep(3)}
                className="rounded-xl"
              >
                Administrar mesas
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Seccion>

          <Seccion icon={ShieldCheck} titulo="Reglas de compra">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ReadOnly
                label="Tipo de acceso"
                value={TIPO_ACCESO_LABEL[valores.tipoAcceso] || "—"}
                testId={EVENTO_VENTA.rlTipoAcceso}
              />
              <ReadOnly
                label="Edad mínima"
                value={
                  valores.edadMinima === "" ||
                  valores.edadMinima === null ||
                  valores.edadMinima === undefined
                    ? "—"
                    : `${valores.edadMinima} años`
                }
                testId={EVENTO_VENTA.rlEdadMinima}
              />
              <ReadOnly
                label="Máx. por usuario"
                value={
                  valores.limiteBoletosPorUsuario
                    ? `${valores.limiteBoletosPorUsuario} boletos`
                    : "—"
                }
                testId={EVENTO_VENTA.rlLimiteBoletos}
              />
            </div>
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                data-testid={EVENTO_VENTA.btnEditarReglas}
                onClick={() => onGoToStep(2)}
                className="rounded-xl"
              >
                Editar configuración
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Seccion>

          <Seccion icon={CalendarRange} titulo="Venta">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ReadOnly
                label="Inicio de venta"
                value={
                  valores.ventaInicioFecha
                    ? `${formatearFechaCorta(valores.ventaInicioFecha)}${valores.ventaInicioHora ? ` · ${valores.ventaInicioHora}` : ""}`
                    : "—"
                }
                testId={EVENTO_VENTA.rlInicioVenta}
              />
              <ReadOnly
                label="Cierre de venta"
                value={
                  valores.ventaCierreFecha
                    ? `${formatearFechaCorta(valores.ventaCierreFecha)}${valores.ventaCierreHora ? ` · ${valores.ventaCierreHora}` : ""}`
                    : "—"
                }
                testId={EVENTO_VENTA.rlCierreVenta}
              />
            </div>
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                data-testid={EVENTO_VENTA.btnEditarVenta}
                onClick={() => onGoToStep(2)}
                className="rounded-xl"
              >
                Editar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Seccion>
        </div>

        <ResumenComercial
          desglose={desglose}
          precios={precios}
          salones={salones}
          valores={valores}
        />
      </div>
    </div>
  );
}

/* -------- Subcomponentes -------- */

function Seccion({ icon: Icon, titulo, descripcion, children }) {
  return (
    <section className="rounded-2xl border hairline bg-card p-6 md:p-7 flex flex-col gap-4">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-accent/10 ring-1 ring-accent/30 text-accent grid place-items-center shrink-0">
          <Icon className="w-4.5 h-4.5" strokeWidth={1.7} />
        </div>
        <div className="min-w-0">
          <h3 className="font-heading text-lg leading-tight">{titulo}</h3>
          {descripcion && (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {descripcion}
            </p>
          )}
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

const TONES = {
  primary: "bg-primary/5 dark:bg-accent/10 text-primary dark:text-accent border-primary/20 dark:border-accent/30",
  success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  warning: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  accent: "bg-accent/15 text-accent border-accent/30",
  danger: "bg-destructive/10 text-destructive border-destructive/30",
  muted: "bg-muted text-muted-foreground border-border/70",
};

function StatBlock({ testId, label, value, tone = "muted" }) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "rounded-xl border px-4 py-3 flex flex-col gap-1",
        TONES[tone] || TONES.muted,
      )}
    >
      <span className="overline opacity-80">{label}</span>
      <span className="font-heading text-2xl leading-none tabular-nums">
        {value}
      </span>
    </div>
  );
}

function ReadOnly({ label, value, testId }) {
  return (
    <div className="rounded-xl border hairline bg-background px-4 py-3">
      <p className="overline text-muted-foreground">{label}</p>
      <p data-testid={testId} className="mt-1 text-sm font-medium truncate">
        {value}
      </p>
    </div>
  );
}

function ResumenComercial({ desglose, precios, salones, valores }) {
  return (
    <aside
      data-testid={EVENTO_VENTA.resumen}
      className="rounded-2xl border hairline bg-card p-6 flex flex-col gap-5 h-fit lg:sticky lg:top-24"
    >
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/5 dark:bg-accent/10 text-primary dark:text-accent ring-1 ring-primary/20 dark:ring-accent/30 grid place-items-center">
          <ClipboardCheck className="w-4.5 h-4.5" strokeWidth={1.7} />
        </div>
        <div>
          <h3 className="font-heading text-lg leading-tight">Resumen</h3>
          <p className="text-xs text-muted-foreground">
            Vista previa comercial del evento.
          </p>
        </div>
      </header>

      <div>
        <p className="overline text-muted-foreground">Capacidad</p>
        <p className="font-heading text-3xl leading-none mt-1">
          {desglose.capacidad.total}{" "}
          <span className="text-sm text-muted-foreground">lugares</span>
        </p>
      </div>

      <div className="border-t hairline pt-4">
        <p className="overline text-muted-foreground">Precios</p>
        <ul className="mt-2 text-sm space-y-1">
          {salones.length === 0 && (
            <li className="text-muted-foreground text-xs">
              Sin salón definido.
            </li>
          )}
          {salones.map((s) => (
            <li key={s} className="flex justify-between gap-3">
              <span className="text-muted-foreground truncate">{s}</span>
              <span className="tabular-nums font-medium">
                {precios[s] ? fmtDinero(precios[s]) : "$ —"}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t hairline pt-4">
        <p className="overline text-muted-foreground">Ventana de venta</p>
        <div className="mt-2 rounded-xl bg-background border hairline p-3 flex items-center gap-3 flex-wrap text-sm">
          <CalendarRange className="w-4 h-4 text-accent" strokeWidth={1.7} />
          {valores.ventaInicioFecha && valores.ventaCierreFecha ? (
            <>
              <span className="font-medium">
                {formatearFechaCorta(valores.ventaInicioFecha)}
                {valores.ventaInicioHora ? ` · ${valores.ventaInicioHora}` : ""}
              </span>
              <span className="text-muted-foreground">al</span>
              <span className="font-medium">
                {formatearFechaCorta(valores.ventaCierreFecha)}
                {valores.ventaCierreHora ? ` · ${valores.ventaCierreHora}` : ""}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground text-xs">
              Falta definir la ventana en Paso 2.
            </span>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-muted/30 border hairline px-4 py-3 flex items-start gap-2 text-[11px] text-muted-foreground">
        <Info className="w-3.5 h-3.5 shrink-0 text-accent mt-0.5" strokeWidth={1.8} />
        <span>
          Los precios se editan en Paso 1. Aquí sólo se muestran para revisión.
        </span>
      </div>
    </aside>
  );
}

function ResumenAGDato({ label, valor, Icono, testId }) {
  return (
    <div className="rounded-xl border hairline bg-background px-3 py-3 flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {Icono && <Icono className="w-3.5 h-3.5" strokeWidth={1.8} />}
        {label}
      </span>
      <span data-testid={testId} className="text-lg font-heading tabular-nums">
        {valor}
      </span>
    </div>
  );
}
